import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound, Loader2, AlertTriangle, MapPinOff, X, Pencil, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

export default function Users() {
  const [rows, setRows] = useState([]);
  const [locks, setLocks] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", name: "", password: "", role: "teknisi", extra_roles: [] });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [attendanceLocs, setAttendanceLocs] = useState([]);
  const [editingAttendance, setEditingAttendance] = useState(null); // user being edited
  // Fase — Editable Username. Modal edit data karyawan (username/nama/
  // telepon/alamat). Simpan draft di `editForm` supaya bisa Batal tanpa
  // side-effect. `editing` holds the original user object (source of id
  // + old username utk deteksi perubahan). Password TIDAK di modal ini —
  // tetap via tombol Reset PW terpisah.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ username: "", name: "", phone: "", address: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  // Fase 5.6B — deep-link filter `?filter=no-location` dari Dashboard
  // (bucket "Tanpa Lokasi"). Backend `/api/users` sudah admin-only + return
  // full list, jadi filter dilakukan client-side dengan tetap menghormati
  // security scope (koord/teknisi tidak akses halaman ini via priv `users`).
  const [searchParams, setSearchParams] = useSearchParams();
  const noLocFilter = searchParams.get("filter") === "no-location";
  const visibleRows = useMemo(() => {
    if (!noLocFilter) return rows;
    // "Tanpa Lokasi" definisi sama dengan Fase 5.5: allowed_location_ids
    // kosong/null/absent. Semantics identik dengan bucket dashboard.
    return (rows || []).filter((u) => !(u.allowed_location_ids && u.allowed_location_ids.length > 0));
  }, [rows, noLocFilter]);
  const clearNoLocFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    try {
      const [{ data: u }, { data: locs }] = await Promise.all([
        api.get("/users"),
        api.get("/attendance/locations").catch(() => ({ data: [] })),
      ]);
      setRows(u);
      setAttendanceLocs(locs || []);
      const { data: lk } = await api.get("/users/login-locks").catch(() => ({ data: [] }));
      setLocks(Object.fromEntries(lk.map((l) => [l.username, l])));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const unlockLogin = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock-login`);
      toast.success(`Kunci login @${u.username} dibuka`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleAbsensi = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { absensi_enabled: !u.absensi_enabled });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveAttendanceLocs = async (userId, ids) => {
    try {
      await api.patch(`/users/${userId}`, { allowed_location_ids: ids });
      toast.success("Lokasi diperbarui");
      setEditingAttendance(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User dibuat");
      setForm({ username: "", name: "", password: "", role: "teknisi", extra_roles: [] });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      username: u.username || "",
      name: u.name || "",
      phone: u.phone || "",
      address: u.address || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    // Client-side validation — backend re-validates as source of truth.
    const uname = (editForm.username || "").trim().toLowerCase();
    const RE = /^[a-z0-9_-]{3,32}$/;
    if (!RE.test(uname)) {
      toast.error("Username harus 3-32 karakter: huruf kecil, angka, underscore, hyphen (tanpa spasi)");
      return;
    }
    if (!(editForm.name || "").trim()) {
      toast.error("Nama lengkap wajib diisi");
      return;
    }
    // Only send fields that changed to keep payload small AND to avoid
    // "no-op" username update triggering unique-check needlessly.
    const payload = {};
    if (uname !== (editing.username || "")) payload.username = uname;
    if ((editForm.name || "").trim() !== (editing.name || "")) payload.name = editForm.name.trim();
    if ((editForm.phone || "") !== (editing.phone || "")) payload.phone = editForm.phone.trim();
    if ((editForm.address || "") !== (editing.address || "")) payload.address = editForm.address.trim();
    if (Object.keys(payload).length === 0) {
      setEditing(null);
      return;
    }
    setSavingEdit(true);
    try {
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Data karyawan tersimpan");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };


  const del = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User dihapus");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role diperbarui");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Feb 2026 — Toggle extra_roles (billing/kolektor) tanpa admin harus
  // mengetik payload JSON. Admin klik chip → auto PATCH ke backend.
  // Backend `_sanitize_extra_roles` whitelist ["billing","kolektor"]
  // sudah defensive; kita hanya mengirim state akhir setelah toggle.
  const toggleExtraRole = async (u, role) => {
    const current = Array.isArray(u.extra_roles) ? u.extra_roles : [];
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    // Update optimistis di UI supaya klik terasa langsung; rollback bila gagal.
    setRows((prev) => prev.map((x) => x.id === u.id ? { ...x, extra_roles: next } : x));
    try {
      await api.patch(`/users/${u.id}`, { extra_roles: next });
      toast.success(`Fungsi ${role} ${next.includes(role) ? "diaktifkan" : "dinonaktifkan"}`);
      load();
    } catch (e) {
      // rollback
      setRows((prev) => prev.map((x) => x.id === u.id ? { ...x, extra_roles: current } : x));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengubah fungsi tambahan");
    }
  };

  const submitReset = async () => {
    if (!resetting || !resetPw || resetPw.length < 4) return;
    try {
      await api.post(`/users/${resetting.id}/reset-password`, { new_password: resetPw });
      toast.success("Password direset");
      setResetting(null);
      setResetPw("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Administrasi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Manajemen User</h1>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-add-user"
          className="h-10 px-4 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus className="h-4 w-4" /> Tambah User
        </button>
      </div>

      {/* Reminder banner: koordinator tanpa lokasi absensi tidak bisa
          melihat Rekap Absensi teknisi (Fase 3.5 — security scope beralih
          dari wilayah_ids ke allowed_location_ids). Auto-hide bila 0
          koord bermasalah. */}
      {(() => {
        const missing = (rows || []).filter((u) => u.role === "koordinator" && (u.allowed_location_ids || []).length === 0);
        if (missing.length === 0) return null;
        return (
          <div
            className="flex items-start gap-3 rounded-md border border-[#F59E0B] bg-[#FEF3C7] p-3 text-[#92400E]"
            data-testid="banner-koord-no-wilayah"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="text-xs leading-relaxed">
              <div className="font-black uppercase tracking-widest text-[11px] mb-0.5">
                {missing.length} Koordinator belum memiliki lokasi absensi
              </div>
              <div>
                Koordinator berikut akan melihat Rekap Absensi kosong sampai admin meng-<em>assign</em> lokasi absensi:
                <span className="font-semibold"> {missing.map((m) => m.name || m.username).join(", ")}</span>. Klik tombol <strong>Lokasi</strong> pada baris masing-masing untuk mengaturnya.
              </div>
            </div>
          </div>
        );
      })()}

      <div className="bg-white border border-[#E5E7EB] rounded-md">
        {/* Fase 5.6B — banner filter Tanpa Lokasi. Muncul saat deep-link
            `?filter=no-location` aktif; tombol X membersihkan filter. */}
        {noLocFilter && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[#FEF3C7] border-b border-[#FDE68A]" data-testid="users-filter-no-location-banner">
            <div className="flex items-center gap-2 text-sm text-[#92400E]">
              <MapPinOff className="h-4 w-4" />
              <span>Menampilkan user <b>tanpa lokasi</b> ({visibleRows.length}). Klik <b>Lokasi</b> pada baris untuk menetapkan.</span>
            </div>
            <button
              onClick={clearNoLocFilter}
              className="h-7 px-2 rounded-md text-xs font-semibold text-[#92400E] hover:bg-[#FDE68A] flex items-center gap-1"
              data-testid="users-filter-clear"
            >
              <X className="h-3 w-3" /> Hapus filter
            </button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] bg-[#F8F9FA]">
            <tr>
              <th className="px-6 py-3">Username</th>
              <th className="px-6 py-3">Nama</th>
              <th className="px-6 py-3">Role</th>
              <th className="px-6 py-3">Fungsi Tambahan</th>
              <th className="px-6 py-3">Absensi</th>
              <th className="px-6 py-3">Dibuat</th>
              <th className="px-6 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((u) => (
              <tr key={u.id} className="border-t border-[#E5E7EB] hover:bg-[#F8F9FA]" data-testid={`user-row-${u.username}`}>
                <td className="px-6 py-3 font-mono-tab font-semibold">
                  {u.username}
                  {locks[u.username] && (
                    <span className="ml-2 inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-[#FEE2E2] text-[#991B1B] text-[9px] font-black uppercase tracking-widest font-sans align-middle"
                          title={`Terkunci sampai ${new Date(locks[u.username].locked_until).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" })} WIB (IP: ${locks[u.username].ips.join(", ")})`}
                          data-testid={`badge-login-locked-${u.username}`}>
                      <Lock className="h-2.5 w-2.5" /> Terkunci
                    </span>
                  )}
                </td>
                <td className="px-6 py-3">{u.name}</td>
                <td className="px-6 py-3">
                  <select
                    value={u.role}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                    className="h-8 px-2 rounded-md border border-[#E5E7EB] bg-white text-xs font-semibold uppercase tracking-wider"
                    data-testid={`role-${u.username}`}
                  >
                    <option value="admin">admin</option>
                    <option value="koordinator">koordinator</option>
                    <option value="teknisi">teknisi</option>
                    <option value="kolektor">kolektor</option>
                  </select>
                </td>
                <td className="px-6 py-3">
                  {/* Feb 2026 — Chip toggle extra_roles. Admin bisa
                      menyalakan/mematikan fungsi Kolektor & Billing tanpa
                      mengubah primary role. Tidak berlaku untuk admin
                      (semua akses sudah include) & primary role yang sama
                      dengan chip. */}
                  {u.role === "admin" ? (
                    <span className="text-[10px] text-[#9CA3AF]" data-testid={`extra-na-${u.username}`}>—</span>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      {["kolektor", "billing"].map((r) => {
                        const active = (u.extra_roles || []).includes(r);
                        const isPrimary = u.role === r;
                        const label = r === "kolektor" ? "Kolektor" : "Billing";
                        return (
                          <button
                            key={r}
                            type="button"
                            disabled={isPrimary}
                            onClick={() => !isPrimary && toggleExtraRole(u, r)}
                            data-testid={`extra-toggle-${r}-${u.username}`}
                            title={isPrimary ? `Sudah menjadi primary role` : `${active ? "Matikan" : "Nyalakan"} fungsi ${label}`}
                            className={`h-7 px-2.5 rounded-full border text-[10px] font-bold uppercase tracking-widest transition-colors ${
                              isPrimary
                                ? "border-[#E5E7EB] bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                                : active
                                  ? (r === "kolektor"
                                      ? "border-[#065F46] bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0]"
                                      : "border-[#0055FF] bg-[#DBEAFE] text-[#1E3A8A] hover:bg-[#BFDBFE]")
                                  : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F3F4F6]"
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleAbsensi(u)}
                      data-testid={`absensi-toggle-${u.username}`}
                      className={`h-6 w-11 rounded-full transition-colors relative ${u.absensi_enabled ? "bg-[#065F46]" : "bg-[#D1D5DB]"}`}
                      title={u.absensi_enabled ? "Wajib Absensi ON" : "Wajib Absensi OFF"}
                    >
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${u.absensi_enabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                    {u.absensi_enabled && (() => {
                      // Fase 3.5 — allowed_location_ids adalah security
                      // scope koord (Rekap Absensi). Koordinator dengan
                      // 0 lokasi ditandai warning karena akan melihat
                      // rekap kosong sampai admin assign.
                      const lcount = (u.allowed_location_ids || []).length;
                      const needs = u.role === "koordinator" && lcount === 0;
                      return (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEditingAttendance(u)}
                            className={`h-7 px-2 rounded-md border text-[10px] font-bold uppercase tracking-widest hover:bg-[#F0F4FF] ${needs ? "border-[#F59E0B] bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A]" : "border-[#E5E7EB] text-[#0055FF]"}`}
                            data-testid={`absensi-locs-${u.username}`}
                          >
                            Lokasi ({lcount})
                          </button>
                          {needs && (
                            <span
                              title="Koordinator belum punya lokasi absensi — Rekap Absensi kosong sampai di-assign"
                              className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[9px] font-black uppercase tracking-widest"
                              data-testid={`badge-no-wilayah-${u.username}`}
                            >
                              <AlertTriangle className="h-2.5 w-2.5" /> Perlu
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td className="px-6 py-3 text-xs text-[#9CA3AF] font-mono-tab">
                  {u.created_at?.slice(0, 10)}
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {locks[u.username] && (
                      <button
                        onClick={() => unlockLogin(u)}
                        className="h-8 px-2 whitespace-nowrap rounded-md border border-[#FCA5A5] bg-[#FEF2F2] hover:bg-[#FEE2E2] flex items-center gap-1 text-xs font-semibold text-[#991B1B]"
                        data-testid={`btn-unlock-login-${u.username}`}
                      >
                        <LockOpen className="h-3.5 w-3.5" /> Buka Kunci
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(u)}
                      className="h-8 px-2 rounded-md hover:bg-[#F3F4F6] flex items-center gap-1 text-xs font-semibold text-[#4B5563]"
                      data-testid={`btn-edit-user-${u.username}`}
                    >
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => { setResetting(u); setResetPw(""); }}
                      className="h-8 px-2 rounded-md hover:bg-[#F3F4F6] flex items-center gap-1 text-xs font-semibold text-[#4B5563]"
                      data-testid={`btn-reset-${u.username}`}
                    >
                      <KeyRound className="h-3.5 w-3.5" /> Reset PW
                    </button>
                    <button
                      onClick={() => del(u.id)}
                      className="h-8 w-8 rounded-md hover:bg-[#FEE2E2] flex items-center justify-center"
                      data-testid={`btn-del-user-${u.username}`}
                    >
                      <Trash2 className="h-4 w-4 text-[#991B1B]" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add user */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Tambah User Baru</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Username</label>
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required minLength={3} className={inputCls} data-testid="new-user-username" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Nama Lengkap</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={inputCls} data-testid="new-user-name" />
              <div className="text-[10px] text-[#9CA3AF] mt-1">Nama ini akan cocok dengan kolom PIC pada tiket untuk teknisi.</div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Password</label>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={4} className={inputCls} data-testid="new-user-password" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="new-user-role">
                <option value="teknisi">Teknisi</option>
                <option value="koordinator">Koordinator</option>
                <option value="kolektor">Kolektor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {/* Feb 2026 — Fungsi Tambahan (extra_roles) opsional saat
                create. Tidak berlaku bila primary role sudah admin
                (mencakup semua) atau bila primary role sama dengan chip. */}
            {form.role !== "admin" && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Fungsi Tambahan (opsional)</label>
                <div className="flex items-center gap-2">
                  {["kolektor", "billing"].map((r) => {
                    const active = (form.extra_roles || []).includes(r);
                    const isPrimary = form.role === r;
                    const label = r === "kolektor" ? "Kolektor" : "Billing";
                    return (
                      <button
                        key={r}
                        type="button"
                        disabled={isPrimary}
                        onClick={() => {
                          if (isPrimary) return;
                          const cur = form.extra_roles || [];
                          setForm({ ...form, extra_roles: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] });
                        }}
                        data-testid={`new-user-extra-${r}`}
                        className={`h-8 px-3 rounded-full border text-[11px] font-bold uppercase tracking-widest transition-colors ${
                          isPrimary
                            ? "border-[#E5E7EB] bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                            : active
                              ? (r === "kolektor"
                                  ? "border-[#065F46] bg-[#D1FAE5] text-[#065F46]"
                                  : "border-[#0055FF] bg-[#DBEAFE] text-[#1E3A8A]")
                              : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F3F4F6]"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-[#9CA3AF] mt-1">
                  Contoh: Teknisi + <b>Kolektor</b> = teknisi lapangan yang juga menagih pelanggan (satu akun).
                </div>
              </div>
            )}
            <DialogFooter>
              <button
                type="submit"
                disabled={saving}
                data-testid="new-user-submit"
                className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold flex items-center gap-2 transition-colors"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Simpan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>


      {/* Fase — Editable Username. Modal Edit Data Karyawan.
          Semua field dapat diedit KECUALI role/absensi/lokasi yang
          tetap menggunakan flow inline existing (dropdown role di tabel,
          toggle absensi, tombol Lokasi) supaya UI tidak duplicate. */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">Edit Data Karyawan</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Username <span className="text-[#DC2626]">*</span></label>
              <input
                type="text"
                required
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value.replace(/\s+/g, "") })}
                minLength={3}
                maxLength={32}
                pattern="^[a-z0-9_-]{3,32}$"
                className={`${inputCls} font-mono-tab`}
                data-testid="edit-user-username"
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-[11px] text-[#6B7280] mt-1">Username digunakan untuk login dan harus unik. Huruf kecil, angka, underscore, hyphen. Tanpa spasi.</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Nama Lengkap <span className="text-[#DC2626]">*</span></label>
              <input
                type="text"
                required
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                maxLength={80}
                className={inputCls}
                data-testid="edit-user-name"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Nomor Telepon</label>
              <input
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="08xxxxxxxxxx"
                className={inputCls}
                data-testid="edit-user-phone"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Alamat</label>
              <textarea
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                rows={3}
                className={`${inputCls} h-auto py-2 resize-y`}
                data-testid="edit-user-address"
              />
            </div>
            <p className="text-[11px] text-[#9CA3AF]">
              Role, Absensi &amp; Lokasi diatur langsung dari tabel — tetap seperti sebelumnya.
            </p>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-10 px-4 rounded-md border border-[#E5E7EB] text-sm font-semibold text-[#4B5563] hover:bg-[#F3F4F6]"
                data-testid="btn-cancel-edit-user"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={savingEdit}
                className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white font-semibold flex items-center gap-1.5"
                data-testid="btn-save-edit-user"
              >
                {savingEdit && <Loader2 className="h-4 w-4 animate-spin" />}
                Simpan Perubahan
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">
              Reset Password: <span className="font-mono-tab">{resetting?.username}</span>
            </DialogTitle>
          </DialogHeader>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Password Baru</label>
            <input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} minLength={4} className={inputCls} data-testid="reset-pw-input" />
          </div>
          <DialogFooter>
            <button
              onClick={submitReset}
              disabled={resetPw.length < 4}
              data-testid="reset-pw-submit"
              className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-60 text-white font-semibold"
            >
              Reset
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Attendance locations picker */}
      <Dialog open={!!editingAttendance} onOpenChange={(v) => !v && setEditingAttendance(null)}>
        <DialogContent data-testid="edit-attendance-locs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold tracking-tight">
              Lokasi Absensi: <span className="font-mono-tab">{editingAttendance?.username}</span>
            </DialogTitle>
          </DialogHeader>
          {editingAttendance && (() => {
            const currentIds = new Set(editingAttendance.allowed_location_ids || []);
            const toggle = (id) => {
              const next = new Set(currentIds);
              if (next.has(id)) next.delete(id); else next.add(id);
              setEditingAttendance({ ...editingAttendance, allowed_location_ids: Array.from(next) });
            };
            return (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {attendanceLocs.length === 0 && (
                  <div className="text-sm text-[#9CA3AF] py-4">Belum ada lokasi absensi. Tambahkan di Pengaturan Aplikasi.</div>
                )}
                {attendanceLocs.map((l) => (
                  <label key={l.id} className="flex items-start gap-2 p-2 rounded hover:bg-[#F8F9FA] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(editingAttendance.allowed_location_ids || []).includes(l.id)}
                      onChange={() => toggle(l.id)}
                      data-testid={`loc-chk-${l.code}`}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{l.name} <span className="text-[#9CA3AF] font-mono-tab">· {l.code}</span> {!l.active && <span className="text-[10px] text-[#991B1B]">(nonaktif)</span>}</div>
                      <div className="text-[11px] text-[#6B7280] font-mono-tab">R {l.radius_meter}m · Acc ≤{l.accuracy_threshold_meter}m</div>
                    </div>
                  </label>
                ))}
              </div>
            );
          })()}
          <DialogFooter>
            <button
              onClick={() => saveAttendanceLocs(editingAttendance.id, editingAttendance.allowed_location_ids || [])}
              className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold"
              data-testid="save-attendance-locs"
            >
              Simpan Lokasi
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Wilayah (deprecated Fase 3.5) — dihapus. Security scope
          koordinator dan filter cakupan Rekap Absensi kini berbasis
          allowed_location_ids yang di-assign lewat tombol "Lokasi" di
          atas. */}
    </div>
  );
}
